import React from "react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";

/**
 * Login — canonical Wikificial sign-in surface.
 *
 * Uses the shared <PageHero variant="bare" /> so the eyebrow / title / sub
 * treatment matches every other page (34px H1, accent eyebrow with dot,
 * muted 15px sub). The Google button + supporting copy sit directly under
 * the hero.
 *
 * Google auth is Emergent-managed OAuth — user is redirected off-site to
 * auth.emergentagent.com. That page's badge/matrix background is NOT
 * editable from this codebase. See Master Spec §5 for the escape path
 * (bring-your-own-Google-OAuth) if we ever want to fully white-label.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS.
 */
export default function Login() {
  const onGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6" data-testid="login-page">
      <div className="w-full max-w-[440px]">
        {/* Brand lock-up */}
        <Link to="/" className="inline-flex items-center gap-2 mb-8" data-testid="login-brand">
          <span
            className="font-display inline-flex items-center justify-center rounded-full text-white leading-none"
            style={{ width: 24, height: 24, background: "#000000", fontSize: 12, fontWeight: 700 }}
            aria-hidden="true"
          >
            W
          </span>
          <span className="text-[20px] font-semibold text-[#0B1220] leading-none tracking-tight">
            Wikifi
          </span>
        </Link>

        <PageHero
          testId="login-hero"
          variant="bare"
          eyebrow="Welcome back"
          title="Sign in to Wikifi."
          sub="The Encyclopedia of AI. Save favorites, run THE ALGORITHM, and unlock Wikifi — the multi-model synthesis engine."
        />

        {/* Google (active) — secondary style (44px, 10px radius) */}
        <button
          onClick={onGoogle}
          className="w-full btn-ghost justify-center"
          data-testid="google-login-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-[13px] text-[#6B7280] mt-6 leading-relaxed">
          By signing in you agree to Wikifi's{" "}
          <Link to="/terms" className="text-[#0B1220] underline underline-offset-2" data-testid="login-terms-link">terms</Link>{" "}
          and{" "}
          <Link to="/privacy" className="text-[#0B1220] underline underline-offset-2" data-testid="login-privacy-link">privacy policy</Link>.
          We never share your data.
        </p>
      </div>
    </div>
  );
}
